<template>
  <portal v-if="showTitle" :to="portalTitleKey">
    <img
      alt=""
      class="authority-service-logo"
      src="/svg/authority-services/did-logo.svg"
    />
  </portal>
  <div class="d-flex flex-column" style="width: 100%">
    <v-card class="mb-4 pa-4" color="grey-lighten-4" flat rounded="lg">
      <v-alert
        class="warning-message mb-4"
        color="warning"
        rounded
        variant="outlined"
      >
        <div class="d-flex flex-row align-center">
          <v-icon class="mr-2" color="warning" size="14"
            >fas fa-triangle-exclamation</v-icon
          >
          <div
            class="d-flex flex-column align-center"
            data-test-id="authentication-message"
          >
            {{ i18n.t('dialogs.identify_warning.alert_text_mobile_id') }}
          </div>
        </div>
      </v-alert>
      <div class="warning-hint" data-test-id="mobile-id-warning-phone-number">
        {{ i18n.t('dialogs.identify_warning.hint_mobile_id') }}
        <span class="warning-hint">
          <a
            class="text-primary"
            href="https://check-signature.scapp.swisscom.com/"
            rel="noopener"
            style="text-decoration: none"
            target="_blank"
          >
            {{ i18n.t('dialogs.identify_warning.check_phone_number') }}
          </a>
        </span>

        <p v-if="showMobileIdAdditionalInfo" class="pt-4">
          {{
            i18n.t(
              'dialogs.identify_warning.alert_text_mobile_id_not_activated_hint_prefix'
            )
          }}
          <a class="more-information" :href="link" target="_blank">
            {{
              i18n.t(
                'dialogs.identify_warning.alert_text_mobile_id_not_activated_hint_link'
              )
            }} </a
          >.
        </p>
      </div>
    </v-card>
    <template v-if="canUseDeepID">
      <div class="mb-3 warning-subtitle">
        {{ i18n.t('dialogs.identify_warning.subtitle') }}
      </div>

      <v-card class="deep-id-card" rounded="lg" variant="outlined">
        <!-- DISABLE EsLint here because of https://github.com/vuetifyjs/eslint-plugin-vuetify/issues/83 -->
        <!-- eslint-disable -->
        <v-img
          src="/svg/authority-services/did-logo.svg"
          height="20"
          contain
          position="left"
          class="mb-4"
        />
        <!-- eslint-enable -->
        <div class="deep-id-overline">
          {{ i18n.t('dialogs.identify_warning.overline') }}
        </div>
        <div class="deep-id-text">
          {{ i18n.t('dialogs.identify_warning.deep_id_text') }}
        </div>
      </v-card>
    </template>

    <portal v-if="showActions" :to="portalActionsKey">
      <VBtnSecondary
        :text="i18n.t('buttons.cancel')"
        variant="outlined"
        @click="handleCancel"
      />
      <VBtnPrimary
        v-if="canUseDeepID"
        color="primary"
        data-test-id="mobile-id-warning-continue-with-deep-id"
        :text="i18n.t('buttons.continue_with_deep_id')"
        @click="continueWithDeepID"
      />
    </portal>
  </div>
</template>

<script setup lang="ts">
import AuthService from '@/services/authService.ts';
import { BetaFeature } from '@/types/enums/BetaFeature';
import { computed } from 'vue';
import { useEmitter } from '@/composables/useEmitter';
import { useI18n } from 'vue-i18n';

defineProps({
  showActions: {
    type: Boolean,
    default: true
  },
  showTitle: {
    type: Boolean,
    default: true
  },
  showMobileIdAdditionalInfo: {
    type: Boolean,
    default: false
  },
  portalActionsKey: {
    type: String,
    default: 'identify-dialog-actions'
  },
  portalTitleKey: {
    type: String,
    default: 'identify-dialog-title'
  }
});

const emit = defineEmits(['close-dialog', 'cancel']);

const emitter = useEmitter();
const i18n = useI18n();

const canUseDeepID = computed(
  () =>
    import.meta.env.VITE_AUTHORITY_SERVICE_ENABLED === 'true' ||
    AuthService.hasBetaFeature(BetaFeature.DEEP_ID_SIGN)
);

const link = computed(() => {
  const locale = i18n.locale.value || 'en';
  return `https://www.mobileid.ch/${locale}/login?desired-workflow=activation`;
});

function closeDialog() {
  emit('close-dialog');
}
function handleCancel() {
  emit('cancel');
}
function continueWithDeepID() {
  emitter.$emit('set-authority-service', 'did');
  closeDialog();
}
</script>

<style scoped lang="scss">
.authority-service-logo {
  height: 20px;
}

.warning-title {
  text-align: center;
  font-weight: 600;
  font-size: 22px;
  line-height: 28px;
  color: black;
}

.deep-id-card {
  padding: 16px;
  background-color: white;
  border-color: rgb(var(--v-theme-primary)) !important;
  box-shadow: 0 5px 10px rgba(0, 0, 0, 0.15) !important;
}

.warning-message {
  background-color: white !important;
  font-weight: 400;
  font-size: 12px;
  line-height: 16px;
  padding: 12px;
}

.warning-subtitle {
  font-weight: 600;
  font-size: 14px;
  line-height: 20px;
  color: black;
  text-align: left !important;
}

.warning-hint {
  font-weight: 400;
  font-size: 12px;
  line-height: 16px;
  color: black;
}

.deep-id-overline {
  font-weight: 600;
  font-size: 13px;
  line-height: 16px;
  color: black;
  margin-bottom: 8px;
}

.deep-id-text {
  font-weight: 400;
  font-size: 12px;
  line-height: 16px;
  color: black;
}
</style>
